import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, noChange, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { confirmDialog } from '../confirm-dialog.js';
import { ConnectionNoteNotice } from '../connection-note-notice.js';
import { sdrRouter } from '../router/app-router.js';
import { toast } from '../toast.js';
import type { ParsedDocument } from '../wizard/document-parser.js';
import type { DocumentUploadState } from '../wizard/document-upload.js';
import {
  type AccountSeed,
  draftFromCampaign,
  hasMeaningfulData as draftHasMeaningfulData,
  hasSaveworthyData as draftHasSaveworthyData,
  manualSteps as manualStepsOf,
  noLinkedinToMoveTo,
  placeholderGoesToLinkedin,
  withAssignments,
  withEmailAccountsByAie,
  withLanguage,
  withLinkedinAccountIdsByAie,
  withLinkedinPlaceholder,
  withSequence,
  withTranslatedStep,
} from '../wizard/logic/draft.js';
import {
  appliesOnLeave,
  leaveAction,
  regenerationStartedMessage,
  SAVE_DRAFT_QUESTION,
  sharedRun,
} from '../wizard/logic/leave.js';
import {
  type Answer,
  type CampaignLoad,
  firstLoad,
  LOAD_FAILED_TITLE,
  LOADING_NOTE,
  NOT_LOADED_NOTE,
  readCampaignAnswer,
  readLeadsAnswer,
  saveRefusal,
  stepsHeldBy,
} from '../wizard/logic/load.js';
import {
  EDIT_DEBOUNCE_MS,
  MAX_STEP_SUBMIT_ATTEMPTS,
  newStepBody,
  offeringBody,
  planSequenceEdit,
  STEP_SUBMIT_RETRY_MS,
  stepAddedToast,
  stepContentBody,
  stepReadyToCreate,
  stepReadyToUpdate,
  stepsRemovedMessage,
} from '../wizard/logic/prepared-edits.js';
import {
  salesRepAccountGaps as accountGapsOf,
  salesRepApplyGate as applyGateOf,
  launchedRemovalBlocked,
} from '../wizard/logic/sales-reps.js';
import { campaignCreateBody, campaignUpdateBody, leadsGoWithUpdate } from '../wizard/logic/save-payload.js';
import {
  stepBlockReason as blockReasonOf,
  keptSteps,
  SALES_REP_ACCOUNT_BLOCK,
  stepMove,
  stepNavigation,
  stepperItems,
  WIZARD_STEPS,
  type WizardStepKey,
} from '../wizard/logic/steps.js';
import { hasRepSetChange, senderAccountApplyAction, shouldAutoApplySalesReps } from '../wizard/sender-account-plan.js';
import type { CampaignDraft } from '../wizard/types.js';
import {
  channelsRequiringAccount,
  connectStepCarriesANote,
  deriveCampaignType,
  emptyCampaignDraft,
  previewSenderId,
} from '../wizard/types.js';
import { WizCampaignType } from '../wizard/wiz-campaign-type.js';
import { WizLeads } from '../wizard/wiz-leads.js';
import { WizManageLeads } from '../wizard/wiz-manage-leads.js';
import { WizOffering } from '../wizard/wiz-offering.js';
import { WizSchedule } from '../wizard/wiz-schedule.js';
import { WizSequence } from '../wizard/wiz-sequence.js';
import { WizSummary } from '../wizard/wiz-summary.js';
import { loadFailure } from './dashboard-load.js';

/** Guard function called by the routing hook before leaving the wizard. */
export let wizardBeforeLeave: (() => Promise<boolean>) | null = null;

/** The leave in flight, so concurrent calls share the same modal (logic/leave.ts, R17a3). */
const wizardLeave = sharedRun<boolean>();

/**
 * Serializes draft saves so a burst of autosaves (or autosave + manual save)
 * can never create two campaigns. The very first save POSTs a new campaign;
 * `wizardCreatedId` remembers the returned id so every later save is a PUT.
 */
let wizardSaveInFlight: Promise<string | null> | null = null;
let wizardCreatedId: string | null = null;

/** The steps (logic/steps.ts), each with its icon. */
const STEP_ICONS: Record<WizardStepKey, string> = {
  leads: 'icon-[material-symbols--group]',
  offering: 'icon-[material-symbols--storefront]',
  schedule: 'icon-[material-symbols--schedule]',
  type: 'icon-[material-symbols--support-agent]',
  sequence: 'icon-[material-symbols--account-tree]',
  summary: 'icon-[material-symbols--summarize]',
};
const STEPS = WIZARD_STEPS;

export type { WizardStepKey };

/** Save campaign draft to API. Returns the saved _id. Throws when the server
 * rejects the save so callers can surface the failure to the user. */
const saveDraftToApi = async (draft: CampaignDraft): Promise<string | null> => {
  // The bodies, and when the leads go along, are logic/save-payload.ts's.
  if (draft._id) {
    const resp = await fetch(`/api/campaigns/${encodeURIComponent(draft._id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(campaignUpdateBody(draft)),
    });
    if (!resp.ok) throw new Error(`Update failed (HTTP ${resp.status})`);
    if (leadsGoWithUpdate(draft)) {
      const leadsResp = await fetch(`/api/campaigns/${encodeURIComponent(draft._id)}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leads: draft.leads }),
      });
      if (!leadsResp.ok) throw new Error(`Saving leads failed (HTTP ${leadsResp.status})`);
    }
    return draft._id;
  } else {
    const resp = await fetch('/api/campaigns', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(campaignCreateBody(draft, new Date())),
    });
    const data = await resp.json().catch(() => null);
    if (!resp.ok || !data?.ok) throw new Error(data?.error || `Create failed (HTTP ${resp.status})`);
    const savedId = data.campaign?._id ? String(data.campaign._id) : null;
    // Save leads for the newly created campaign
    if (savedId && draft.leads.length > 0) {
      const leadsResp = await fetch(`/api/campaigns/${encodeURIComponent(savedId)}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leads: draft.leads }),
      });
      if (!leadsResp.ok) throw new Error(`Saving leads failed (HTTP ${leadsResp.status})`);
    }
    return savedId;
  }
};

/** GET `url`: its status and JSON body (null when it is not JSON), or null when no answer came. */
const answerOf = async (url: string): Promise<Answer | null> => {
  let resp: Response;
  try {
    resp = await fetch(url);
  } catch {
    return null;
  }
  return { status: resp.status, body: await resp.json().catch(() => null) };
};

export const CampaignWizard = stateful(({ campaignId }: { campaignId?: string }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [draft, setDraft] = useState<CampaignDraft>(emptyCampaignDraft());
  // Stable ref to the latest draft for ASYNC writers (the sequence step
  // persists generated samples once the LLM responds — possibly after the
  // user already navigated to another wizard step). Merging into a
  // render-time `draft` closure there would wipe edits made in between.
  const [liveDraft] = useState({ current: draft });
  liveDraft.current = draft;
  // Editing an existing campaign that already left 'draft' (ready_to_launch /
  // preparing_to_launch — the only statuses the campaign-detail page's Edit
  // button is shown for besides 'draft' itself). Leads/messages already exist
  // for these, so several steps behave differently: Leads becomes read-only,
  // adding a sequence step routes through the dedicated endpoint (which
  // generates messages for it) instead of relying on a from-scratch prepare,
  // Sales Rep changes route through the dedicated regenerate-aware endpoint,
  // and the final step skips /api/campaign/prepare entirely (see below).
  const isEditingNonDraft = !!draft.status && draft.status !== 'draft';
  // Step ids that already exist server-side with generated messages (snapshot
  // taken when an existing campaign loads — see the load effect below). Used
  // in edit mode to tell "a step the user just added, still being typed into"
  // apart from "a step that already has messages and can't be removed here."
  const [confirmedStepIds] = useState({ current: new Set<string>() });
  // Server-side truth of the Sales Rep assignments (snapshotted at load,
  // advanced on every successful /sales-reps apply). While editing a non-draft
  // campaign the draft may run ahead of it — the diff drives the
  // "Apply changes" bar in the Sales Rep step.
  const [appliedAssignments] = useState<{ current: CampaignDraft['aiEmployeeAssignments'] }>({ current: [] });
  // Same idea for the per-rep sending accounts: past 'draft' they travel with
  // the assignment through /sales-reps (never through the autosave), so the
  // Apply bar has to diff them too — otherwise picking the new rep's account
  // would look like an unsaved change that nothing ever saves.
  const [appliedEmailAccountsByAie] = useState<{ current: Record<string, string[]> }>({ current: {} });
  const [appliedLinkedinAccountIdsByAie] = useState<{ current: Record<string, string[]> }>({ current: {} });
  const [applyingReps, setApplyingReps] = useState(false);
  // Debounce timer for auto-applying Sales-Rep / sender-account changes in edit
  // mode. Past 'draft' the rep block does NOT ride the plain autosave
  // (saveDraftToApi omits it — see there); this is what makes a rep change feel
  // like every other field instead of waiting on an "Apply" button the user
  // could forget (LinkedIn-UAT 4 Sep): it commits itself once the picker settles
  // into a valid, applicable state. Also re-used for a short retry when the
  // dedicated endpoint is briefly busy regenerating an earlier edit.
  const [pendingRepsTimer] = useState({ current: null as ReturnType<typeof setTimeout> | null });
  // Debounce timers for not-yet-confirmed new steps, keyed by step id — mirrors
  // the 1.5s autosave debounce so the create-step endpoint isn't hit on every
  // keystroke while the user is still typing the step's prompt.
  const [pendingStepTimers] = useState(new Map<string, ReturnType<typeof setTimeout>>());
  // Debounce timer for Offering edits in edit mode — mirrors pendingStepTimers.
  const [pendingOfferingTimer] = useState({ current: null as ReturnType<typeof setTimeout> | null });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Where the load of an edited campaign stands (logic/load.ts). Until it has
  // loaded, the draft is the empty one every wizard starts with, not the
  // campaign: nothing is saved then, and nothing after a load that failed.
  const [load, setLoad] = useState<CampaignLoad>(firstLoad(campaignId));
  const loaded = load.state === 'loaded';
  // Why Save, the autosave and the leave may not send the draft now, or null (logic/load.ts).
  const saveBlocked = saveRefusal({ campaignId, load, draftId: draft._id });
  // Until an edited campaign is there, no step rule is: the stepper is held with why (R17a3 review).
  const stepsHeld = stepsHeldBy(load);
  // Track whether this draft is persisted in the database
  const [persisted, setPersisted] = useState(!!campaignId);
  // Track the furthest step the user has navigated to.
  // A step only shows a green checkmark if the user has advanced past it.
  const [maxStepReached, setMaxStepReached] = useState(campaignId ? STEPS.length - 1 : 0);
  // Whether the Sales Rep step may show its per-rep "missing account" hints yet.
  // Stays false until the user actually tries to advance past a missing-account
  // gate, so a rep just ticked is never marked before they can pick an account
  // (LinkedIn-UAT follow-up 2026-09-19). Reset on every rep-set change so a
  // freshly added/removed rep starts quiet again. Display only — the real gate
  // (stepBlockReason / salesRepApplyGate) is unaffected.
  const [revealRepGaps, setRevealRepGaps] = useState(false);
  // Track whether the campaign was successfully finished — skip save-on-leave.
  const [finished, setFinished] = useState(false);
  // True while the Offering step is parsing a document/website or the AI is
  // analyzing — navigation is blocked so the in-progress result isn't lost.
  const [offeringBusy, setOfferingBusy] = useState(false);
  // Timestamp of the last successful save — drives the visible save indicator.
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(campaignId ? Date.now() : null);
  // Edit-mode only: some edits (Sales-Rep move, step-content edit, offering
  // change) DEFER their message regeneration to wizard exit. A toast per edit
  // stacked up as the user made several changes (UAT 4 Sep). Instead we keep ONE
  // quiet, in-place indicator that a regeneration is pending, and fire the single
  // bundled toast on exit (flushPendingRegeneration). Reset once flushed.
  const [regenPending, setRegenPending] = useState(false);
  // Offering upload working-set, lifted out of the step component so fetched
  // websites / parsed documents survive step navigation. Since R17a the step is
  // kept for the whole visit anyway (keptSteps below); lifted, it also survives
  // the step being built anew, which only leaving the wizard does now.
  const [offeringUploadState, setOfferingUploadState] = useState<DocumentUploadState>({
    files: [],
    websites: [],
    cloudFiles: [],
  });
  const [offeringParsedDocs, setOfferingParsedDocs] = useState<ParsedDocument[]>([]);
  // The steps shown in this visit (keptSteps).
  const [visitedSteps] = useState(new Set<number>());

  /** Whether the user has entered anything; and whether a new draft is worth creating yet (logic/draft.ts). */
  const hasMeaningfulData = (): boolean => draftHasMeaningfulData(draft);
  const hasSaveworthyData = (): boolean => draftHasSaveworthyData(draft);

  /**
   * Persist the draft (create on first save, update afterwards). All saves go
   * through here so concurrent calls are serialized and a new campaign is only
   * ever created once (see `wizardSaveInFlight` / `wizardCreatedId`).
   */
  const persistDraft = async (d: CampaignDraft): Promise<string | null> => {
    // The one gate (logic/load.ts): an edited campaign is sent only once it has
    // loaded and `d` is the draft read from it. Save is disabled and the leave
    // and the autosave do not ask meanwhile, so a refusal here means a caller
    // that went around them: said in the console, nothing sent.
    const refused = saveRefusal({ campaignId, load, draftId: d._id });
    if (refused) {
      console.warn('[wizard] save refused: the edited campaign has not loaded', { campaignId, load: load.state });
      return null;
    }
    if (wizardSaveInFlight) {
      try {
        await wizardSaveInFlight;
      } catch {
        /* ignore — start our own save regardless */
      }
    }
    const effectiveId = d._id ?? wizardCreatedId ?? undefined;
    const run = saveDraftToApi(effectiveId ? { ...d, _id: effectiveId } : d);
    wizardSaveInFlight = run;
    setSaving(true);
    try {
      const savedId = await run;
      if (savedId) {
        if (!wizardCreatedId) wizardCreatedId = savedId;
        if (!d._id) {
          setDraft({ ...d, _id: savedId });
          setPersisted(true);
        }
        setLastSavedAt(Date.now());
      }
      setSaveError(null);
      return savedId;
    } catch (err) {
      // Surface the failure — silently pretending the draft is saved cost the
      // testers their campaign settings in the last iteration.
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setSaveError(msg);
      toast.error(`Saving campaign failed: ${msg}`);
      return null;
    } finally {
      if (wizardSaveInFlight === run) wizardSaveInFlight = null;
      setSaving(false);
    }
  };

  /**
   * Called before leaving the wizard.
   * - Persisted drafts: auto-save then allow.
   * - New campaign with data: ask user whether to save as draft.
   * - No data: allow silently.
   * Always returns true (navigation is never blocked).
   */
  /**
   * Starts the message regeneration this editing session queued up. Edits made
   * while editing an already-prepared campaign (Sales Reps, sequence steps,
   * offering) only FLAG the affected messages server-side — nothing is
   * generated while the user is still in the stepper, so the campaign keeps its
   * editable status and one edit can follow another. Leaving the wizard is what
   * triggers the single generation pass.
   */
  const flushPendingRegeneration = async (idToFlush: string): Promise<void> => {
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(idToFlush)}/messages/regenerate-pending`, {
        method: 'POST',
      });
      const data = await resp.json().catch(() => null);
      // The queue is being drained now — clear the "changes pending" indicator so
      // it never lingers, and fire the ONE bundled toast that replaces the
      // per-edit ones.
      setRegenPending(false);
      if (data?.ok && data.queued > 0) {
        toast.success(regenerationStartedMessage(data.queued));
      }
    } catch {
      // Non-fatal: the queue is persisted server-side, so it can be drained later.
    }
  };

  /**
   * One in-place "changes will regenerate on exit" signal instead of a toast per
   * edit. Editing an already-prepared campaign defers regeneration to exit (see
   * flushPendingRegeneration); a toast on every step / offering / rep edit used
   * to stack into a wall of near-identical messages (UAT 4 Sep). This flips a
   * single quiet indicator in the header instead, and the one bundled toast
   * still fires on leave.
   */
  const noteDeferredRegen = (queued: number): void => {
    if (queued > 0) setRegenPending(true);
  };

  // Concurrent callers share one leave and one modal; a leave that decides at
  // once (nothing to save) no longer stays behind as the leave in flight
  // (sharedRun, logic/leave.ts).
  const attemptLeave = (): Promise<boolean> =>
    wizardLeave.run(async () => {
      // What leaving does is logic/leave.ts's; carrying it out is the page's.
      const action = leaveAction({
        finished,
        notLoaded: !!campaignId && !loaded,
        saved: persisted || !!draft._id || !!wizardCreatedId,
        meaningful: hasMeaningfulData(),
      });
      if (action === 'leave') return true;
      if (action === 'save') {
        await persistDraft(draft);
        const editedId = draft._id ?? wizardCreatedId;
        if (editedId && appliesOnLeave(isEditingNonDraft, editedId)) {
          // A valid Sales-Rep change made in the last debounce window would
          // otherwise be cancelled by the effect's cleanup on unmount and
          // silently lost — exactly the "you think it saved but it didn't"
          // trap the auto-apply removes. Commit it here first, synchronously.
          if (pendingRepsTimer.current) {
            clearTimeout(pendingRepsTimer.current);
            pendingRepsTimer.current = null;
          }
          if (salesRepAutoApplyReady()) await applySalesRepChanges({ auto: true });
          await flushPendingRegeneration(editedId);
        }
        return true;
      }
      const wantsSave = await confirmDialog(SAVE_DRAFT_QUESTION.message, {
        title: SAVE_DRAFT_QUESTION.title,
        confirmLabel: SAVE_DRAFT_QUESTION.confirmLabel,
        confirmClass: 'btn-primary',
      });
      if (wantsSave) {
        await persistDraft(draft);
      }
      return true;
    });

  // Update the guard ref on every render so it captures latest state
  wizardBeforeLeave = attemptLeave;
  useEffect(() => {
    // Seed the create-guard: editing an existing campaign already has an id, a
    // fresh wizard starts with none. Reset on unmount so a subsequent "New
    // campaign" in the same session can't reuse a stale id.
    wizardCreatedId = campaignId ?? null;
    wizardSaveInFlight = null;
    wizardLeave.reset();
    return () => {
      wizardBeforeLeave = null;
      wizardCreatedId = null;
      wizardSaveInFlight = null;
      wizardLeave.reset();
    };
  }, []);

  // The leave hook: the router runs it before every navigation away — the nav, links, goTo and,
  // since R12c, back/forward too (decision c1). The effect returns what onBeforeLeave returns, so
  // the wizard's cleanup removes this hook and no other.
  useEffect(
    () =>
      sdrRouter.onBeforeLeave(async () => {
        if (wizardBeforeLeave) await wizardBeforeLeave();
        return true;
      }),
    [],
  );

  /**
   * Required-field gate for a step (logic/steps.ts): the reason the user can't
   * advance past it yet, or null. One source of truth for the "Next" button,
   * the inline hint, the stepper dots and the step-move guard. The reps'
   * coverage is checked on the latest draft.
   */
  const stepBlockReason = (key: WizardStepKey | undefined): string | null =>
    blockReasonOf(key, draft, offeringBusy, liveDraft.current);
  const reasonAt = (step: number): string | null => stepBlockReason(STEPS[step]?.key);

  const goToStep = async (nextStep: number) => {
    const move = stepMove({
      from: currentStep,
      to: nextStep,
      offeringBusy,
      hasApolloSearch: !!draft.apolloSearchInput,
      reasonAt,
    });
    if (move.kind === 'stay') return;
    if (move.kind === 'refuse') {
      const { reason } = move;
      // The soft Sales Rep gate's click is also what first reveals the per-rep hints.
      if (reason === SALES_REP_ACCOUNT_BLOCK) setRevealRepGaps(true);
      toast.error(reason);
      return;
    }
    if (move.saveFirst) {
      await persistDraft(draft);
    }
    setCurrentStep(nextStep);
    setMaxStepReached(Math.max(maxStepReached, nextStep));
  };

  // Load existing campaign if editing. A load that fails says so, with Try
  // again (it sets `load` back to loading); it no longer starts the wizard
  // empty for the campaign (R17a3, logic/load.ts).
  useEffect(() => {
    if (!campaignId || load.state !== 'loading') return;
    (async () => {
      const base = `/api/campaigns/${encodeURIComponent(campaignId)}`;
      const campaign = readCampaignAnswer(await answerOf(base));
      if (!campaign.ok) {
        setLoad({ state: 'failed', reason: campaign.reason });
        return;
      }
      // Load leads before building draft to avoid stale-closure issues
      const leads = readLeadsAnswer(await answerOf(`${base}/leads`));
      if (!leads.ok) {
        setLoad({ state: 'failed', reason: leads.reason });
        return;
      }
      // The draft, mended where old campaigns are damaged (logic/draft.ts), and what the server
      // already holds: the steps with messages, and the Sales Rep block the edits are diffed on.
      let read: ReturnType<typeof draftFromCampaign>;
      try {
        read = draftFromCampaign(campaign.value, leads.value);
      } catch {
        setLoad({ state: 'failed', reason: 'The campaign could not be read.' });
        return;
      }
      confirmedStepIds.current = read.stepIds;
      appliedAssignments.current = read.appliedAssignments;
      appliedEmailAccountsByAie.current = read.appliedEmailAccountsByAie;
      appliedLinkedinAccountIdsByAie.current = read.appliedLinkedinAccountIdsByAie;
      setDraft(read.draft);
      setPersisted(true);
      setLoad({ state: 'loaded' });
    })();
  }, [campaignId, load]);

  // Auto-save the draft after edits. Unlike before, this also covers BRAND-NEW
  // drafts: the first save creates the campaign (once it has real, save-worthy
  // data), every later edit updates it. `persistDraft` serializes saves so a
  // burst of edits can never create duplicate campaigns.
  useEffect(() => {
    // Don't fire while an existing campaign is still loading into the draft.
    if (campaignId && !loaded) return;
    if (!hasSaveworthyData()) return;
    const timeout = setTimeout(() => {
      void persistDraft(draft);
    }, 1500);
    return () => clearTimeout(timeout);
  }, [
    draft.name,
    draft.campaignType,
    draft.offering,
    draft.schedule,
    draft.sequence,
    draft.emailAccounts,
    draft.linkedinAccountIds,
    draft.llmModel,
    draft.aiEmployeeId,
    draft.aiEmployeeAssignments,
    draft.emailAccountsByAie,
    draft.linkedinAccountIdsByAie,
    draft.leads,
    draft.autoReply,
    draft.replyPrompt,
    draft.replyLlmModel,
    draft.crmAutoSync,
    draft.language,
    draft.apolloSearchInput,
    draft.projectId,
    persisted,
  ]);

  /**
   * Put the placeholder step on a channel this user can actually send on.
   *
   * Every new draft opens with one EMAIL step (see emptyCampaignDraft), and the
   * Sales Rep step — which comes BEFORE the sequence builder — derives the
   * channels a rep needs an account for from exactly that sequence. So someone
   * who only ever connected LinkedIn was asked for a mailbox they don't have,
   * for a step they never added, before they had even seen the sequence. The
   * placeholder is our guess, not their decision, so we correct it as soon as we
   * know what they have: no mailbox but a LinkedIn seat means the campaign they
   * are about to build starts on LinkedIn.
   *
   * Only ever replaces the untouched placeholder, and only on a brand-new draft.
   */
  useEffect(() => {
    if (campaignId) return;
    void (async () => {
      const linkedinRes = await fetch('/api/accounts/linkedin')
        .then(r => r.json())
        .catch(() => ({ ok: false }));
      // Nothing to re-seed to — leave the email placeholder alone. Checked first
      // so the common email-only user never pays for the second request.
      if (noLinkedinToMoveTo(linkedinRes)) return;
      // Email accounts come from two sources now — Instantly (retiring) and
      // Unipile (the new path). Only re-seed to LinkedIn when we can confirm the
      // user has NEITHER; a failed lookup means we cannot be sure, so leave the
      // placeholder alone rather than push an email user onto LinkedIn.
      const [emailRes, unipileEmailRes] = await Promise.all([
        fetch('/api/accounts/email')
          .then(r => r.json())
          .catch(() => ({ ok: false })),
        fetch('/api/accounts/unipile-email')
          .then(r => r.json())
          .catch(() => ({ ok: false })),
      ]);
      if (!placeholderGoesToLinkedin(linkedinRes, emailRes, unipileEmailRes)) return;
      // The user may have shaped the sequence while those requests were in
      // flight — re-check against the live draft, never the mount-time one.
      const moved = withLinkedinPlaceholder(liveDraft.current);
      if (moved) setDraft(moved);
    })();
  }, []);

  // Keep maxStepReached in sync whenever user navigates
  useEffect(() => {
    if (currentStep > maxStepReached) setMaxStepReached(currentStep);
    // The busy flag is only meaningful on the Offering step — clear it whenever
    // we're somewhere else so a stale value can't permanently block navigation.
    if (STEPS[currentStep]?.key !== 'offering') setOfferingBusy(false);
    // Smoothly scroll the main scroll container back to top when the step changes
    requestAnimationFrame(() => {
      const main = document.getElementById('main-content');
      (main ?? window).scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [currentStep]);

  /** Applies an assignments change to the draft (prunes per-AIE account buckets for reps no longer assigned). */
  const applyAssignmentsChange = (
    aiEmployeeAssignments: CampaignDraft['aiEmployeeAssignments'],
    // Accounts the Sales Rep step wants pre-selected for a rep that was just
    // checked (their single account for a channel this campaign actually
    // uses). It has to ride along here rather than come through a second
    // setter: two setters in one event handler means the later one spreads a
    // stale draft.
    seed?: AccountSeed,
  ) => {
    // A rep-set change starts the missing-account hints quiet again: the user
    // has just ticked/unticked someone and has not yet had a chance to pick an
    // account for them, so nagging now is exactly what this ticket removed. The
    // hints come back the moment they try to advance (goToStep).
    setRevealRepGaps(false);
    // One setDraft (logic/draft.ts), so no stale-closure overwrite drops a change.
    setDraft(withAssignments(liveDraft.current, aiEmployeeAssignments, seed));
  };

  /**
   * Actually creates a not-yet-confirmed step server-side once its debounce
   * fires. `attempt` counts the tries spent waiting out a temporary refusal —
   * see the retry below.
   */
  const submitNewStep = async (stepId: string, attempt = 1) => {
    pendingStepTimers.delete(stepId);
    const step = liveDraft.current.sequence.find(s => s.id === stepId);
    // Gone already (user removed it before the debounce fired) or still empty
    // (nothing worth saving yet — the next edit will re-arm the debounce).
    if (!stepReadyToCreate(step)) return;
    // Undo the optimistic local insert when the server rejects the step —
    // otherwise a ghost step stays visible (and the autosave PUT would even
    // persist it into campaign.sequence) although no messages exist for it.
    const revertStep = () => {
      setDraft(
        withSequence(
          liveDraft.current,
          liveDraft.current.sequence.filter(s => s.id !== stepId),
        ),
      );
    };
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(draft._id!)}/sequence/steps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Our id, not the server's (logic/prepared-edits.ts): the autosave PUT
        // persists campaign.sequence carrying THIS id.
        body: JSON.stringify(newStepBody(step)),
      });
      const data = await resp.json();
      if (!data.ok) {
        // QA #2: the first batch is still generating. That refusal passes by
        // itself within a moment, and throwing the user's typed step away for it
        // meant the step vanished and only a second attempt stuck. Wait for it
        // instead — bounded, so a refusal that is NOT temporary still surfaces.
        if (data.retryable && attempt < MAX_STEP_SUBMIT_ATTEMPTS) {
          if (attempt === 1)
            toast.info(data.error ?? 'The first batch is still generating — saving your step shortly…');
          pendingStepTimers.set(
            stepId,
            setTimeout(() => void submitNewStep(stepId, attempt + 1), STEP_SUBMIT_RETRY_MS),
          );
          return;
        }
        toast.error(data.error ?? 'Failed to add step');
        revertStep();
        return;
      }
      confirmedStepIds.current.add(stepId);
      // Leads missing the new step's contact data skip it; the toast names the
      // way to the full count, pause included (logic/prepared-edits.ts).
      const added = stepAddedToast(data);
      if (added.kind === 'warning') toast.warning(added.message, added.ms);
      else toast.success(added.message);
    } catch {
      toast.error('Failed to add step (network error)');
      revertStep();
    }
  };

  /**
   * Actually persists a remove/reorder of already-confirmed steps. Doesn't
   * touch campaign.sequence (the regular autosave PUT already does that,
   * stepId-safe) — only cleans up CampaignMessage docs for removed steps and
   * re-stamps stepIndex on the rest so it matches their new position.
   * Reverts the optimistic local change on failure so the UI never shows a
   * remove/reorder as applied when the server rejected it.
   */
  const submitSequenceReorder = async (
    removedStepIds: string[],
    orderedStepIds: string[],
    previousSequence: CampaignDraft['sequence'],
  ) => {
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(draft._id!)}/sequence/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderedStepIds, removedStepIds }),
      });
      const data = await resp.json();
      if (!data.ok) {
        toast.error(data.error ?? 'Failed to update the sequence');
        for (const id of removedStepIds) confirmedStepIds.current.add(id); // undo the optimistic removal
        setDraft(withSequence(liveDraft.current, previousSequence));
        return;
      }
      if (removedStepIds.length > 0) {
        toast.success(stepsRemovedMessage(data.removedMessages));
      }
    } catch {
      toast.error('Failed to update the sequence (network error)');
      for (const id of removedStepIds) confirmedStepIds.current.add(id);
      setDraft(withSequence(liveDraft.current, previousSequence));
    }
  };

  /** Actually persists a content edit to an already-confirmed step once its debounce fires. */
  const submitStepContentChange = async (stepId: string) => {
    pendingStepTimers.delete(stepId);
    const step = liveDraft.current.sequence.find(s => s.id === stepId);
    // Gone already, or reverted back to not-confirmed (shouldn't happen, but
    // guard anyway), or emptied out — nothing worth submitting.
    if (!stepReadyToUpdate(step, confirmedStepIds.current)) return;
    try {
      const resp = await fetch(
        `/api/campaigns/${encodeURIComponent(draft._id!)}/sequence/steps/${encodeURIComponent(stepId)}`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(stepContentBody(step)) },
      );
      const data = await resp.json();
      if (!data.ok) {
        toast.error(data.error ?? 'Failed to update step');
        return;
      }
      // No per-edit toast — it stacked. One quiet indicator instead; the single
      // bundled toast fires on exit (flushPendingRegeneration).
      noteDeferredRegen(data.messagesQueued);
    } catch {
      toast.error('Failed to update step (network error)');
    }
  };

  /**
   * Sequence-step changes when editing an already-prepared campaign. Every
   * mutation in wiz-sequence.ts (add/remove/reorder/edit-content) funnels
   * through this single callback. `confirmedStepIds` (snapshotted at load,
   * grown as new steps get confirmed below) tells apart:
   *  - an id already in `confirmedStepIds`: already has generated messages.
   *    Content edits (prompt/subject/manualBody/timing/condition) are applied
   *    locally immediately and debounced before PUTting to the dedicated
   *    per-step endpoint, which regenerates that step's messages — the plain
   *    autosave PUT still fires too (harmless field mirroring, no regen).
   *    Removing a confirmed step, or changing the relative order of confirmed
   *    steps, is applied optimistically and synced via the dedicated reorder
   *    endpoint (cleans up CampaignMessage docs / re-stamps stepIndex — see
   *    submitSequenceReorder above); a failure reverts the local change.
   *  - an id NOT in `confirmedStepIds`: a step added THIS session, still being
   *    typed into. Applied locally immediately (so typing feels normal) and
   *    debounced (mirrors the 1.5s autosave) before POSTing it to the
   *    dedicated add-step endpoint — never persisted with an empty prompt.
   *    Removing it before the debounce fires is always fine (nothing was ever
   *    saved server-side for it).
   */
  const handleSequenceChange = (newSequence: CampaignDraft['sequence']) => {
    if (!isEditingNonDraft || !draft._id) {
      setDraft(withSequence(liveDraft.current, newSequence));
      return;
    }
    const previousSequence = liveDraft.current.sequence;
    // What goes where is logic/prepared-edits.ts's; the timers and requests are the page's.
    const plan = planSequenceEdit({
      previous: previousSequence,
      next: newSequence,
      confirmed: confirmedStepIds.current,
      pending: pendingStepTimers.keys(),
    });
    // A pending (not-yet-confirmed) step the user just removed — cancel its
    // debounce so it's never submitted.
    for (const id of plan.cancelPending) {
      clearTimeout(pendingStepTimers.get(id));
      pendingStepTimers.delete(id);
    }
    for (const id of plan.removedConfirmed) confirmedStepIds.current.delete(id);
    setDraft(withSequence(liveDraft.current, newSequence));
    // Not-yet-confirmed steps: debounce the create-step call. Confirmed steps
    // whose content differs from what's persisted: debounce the content call.
    for (const { id, submit } of plan.debounce) {
      const existingTimer = pendingStepTimers.get(id);
      if (existingTimer) clearTimeout(existingTimer);
      const send = submit === 'create' ? submitNewStep : submitStepContentChange;
      pendingStepTimers.set(
        id,
        setTimeout(() => void send(id), EDIT_DEBOUNCE_MS),
      );
    }
    if (plan.removedConfirmed.length > 0 || plan.orderChanged) {
      void submitSequenceReorder(plan.removedConfirmed, plan.newConfirmedOrder, previousSequence);
    }
  };

  /**
   * Offering changes when editing an already-prepared campaign. The plain
   * autosave PUT would silently update campaign.offering without touching any
   * already-generated message — the dedicated /offering endpoint regenerates
   * every non-manual, non-deferred message against the new product
   * description/talking points instead.
   */
  const submitOfferingChange = async () => {
    pendingOfferingTimer.current = null;
    const offering = liveDraft.current.offering;
    if (!draft._id || !offering.name?.trim()) return;
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(draft._id)}/offering`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(offeringBody(offering)),
      });
      const data = await resp.json();
      if (!data.ok) {
        toast.error(data.error ?? 'Failed to update offering');
        return;
      }
      // No per-edit toast — one quiet indicator; the bundled toast fires on exit.
      noteDeferredRegen(data.messagesQueued);
    } catch {
      toast.error('Failed to update offering (network error)');
    }
  };
  const handleOfferingChange = (offering: CampaignDraft['offering']) => {
    setDraft({ ...liveDraft.current, offering });
    if (!isEditingNonDraft || !draft._id) return;
    if (pendingOfferingTimer.current) clearTimeout(pendingOfferingTimer.current);
    pendingOfferingTimer.current = setTimeout(() => void submitOfferingChange(), EDIT_DEBOUNCE_MS);
  };

  /**
   * Channels this campaign actually sends on. A Sales Rep only needs an
   * account for a channel the sequence uses — an email-only campaign doesn't
   * care that a rep has no LinkedIn seat, and the untouched placeholder step
   * demands nothing (see channelsRequiringAccount).
   */
  const requiredChannels = (): { email: boolean; linkedin: boolean } =>
    channelsRequiringAccount(liveDraft.current.sequence);

  /**
   * Which required channels are missing a sending account on at least one
   * assigned Sales Rep, and what the Apply gate still waits for — on the latest
   * draft (logic/sales-reps.ts).
   */
  const salesRepAccountGaps = (): { email: boolean; linkedin: boolean } => accountGapsOf(liveDraft.current);
  const salesRepApplyGate = (): { hint: string; error: string } | null => applyGateOf(liveDraft.current);

  /**
   * Commits the draft's Sales Rep assignments AND their per-rep sending accounts
   * to the server when editing an already-prepared campaign. The dedicated
   * /sales-reps endpoint re-keys leads to the new split and regenerates the moved
   * leads' messages in their new persona's voice, which a plain autosave would
   * silently skip (saveDraftToApi deliberately omits the whole Sales Rep block
   * for non-draft campaigns for exactly that reason).
   *
   * Called automatically (debounced) once the picker settles into a valid,
   * applicable state — there is no "Apply" button any more (UAT 4 Sep). `auto`
   * keeps that self-commit quiet: it never toasts a validation gate or a no-op,
   * because the picker's inline hint already covers those, and a bare
   * percentage tweak or a blocked removal simply isn't committed.
   */
  const applySalesRepChanges = async (opts?: { auto?: boolean }) => {
    const auto = opts?.auto ?? false;
    if (!draft._id || applyingReps) return;
    const gate = salesRepApplyGate();
    if (gate) {
      if (!auto) toast.error(gate.error);
      return;
    }
    const assignments = liveDraft.current.aiEmployeeAssignments;
    // Snapshot what we actually send, so the applied-state refs below record
    // that and not whatever the user changed while the request was in flight.
    const sentEmailByAie = structuredClone(liveDraft.current.emailAccountsByAie);
    const sentLinkedinByAie = structuredClone(liveDraft.current.linkedinAccountIdsByAie);

    // A launched campaign accepts two kinds of change here:
    //  - a rep-set change (a Sales Rep added, removed, or swapped) falls through
    //    to the full re-split endpoint below. On a launched campaign that
    //    endpoint locks every already-contacted lead to its current rep and
    //    moves only the still-uncontacted ones.
    //  - adding a sender account to a rep already on the campaign takes the
    //    lighter /sender-accounts path, which never re-keys anyone.
    // The rep-set test keys off the SET of rep ids only, never the percentages:
    // the Sales Rep picker re-normalises those on its own, and reading that as a
    // change used to block a perfectly valid account addition (UAT 21 Aug).
    if (draft.launchedAt && !hasRepSetChange(appliedAssignments.current, assignments)) {
      const action = senderAccountApplyAction(
        appliedEmailAccountsByAie.current,
        sentEmailByAie,
        appliedLinkedinAccountIdsByAie.current,
        sentLinkedinByAie,
      );
      if (action === 'blocked-removal') {
        // Not auto-committable — the inline notice in the Sales Rep step explains
        // it. (Auto-apply never reaches here; kept for a defensive direct call.)
        if (!auto)
          toast.error(
            'A sender account can’t be removed from a launched campaign — the leads already sending from it would be ' +
              'stranded. Undo the removal; you can only ADD a sender account here.',
          );
        return;
      }
      if (action === 'nothing-added') {
        if (!auto)
          toast.error(
            'Nothing to apply. On a launched campaign you can add a sender account, or add/remove a Sales Rep — ' +
              'changing only the percentage split isn’t applied here.',
          );
        return;
      }
      setApplyingReps(true);
      try {
        const resp = await fetch(`/api/campaigns/${encodeURIComponent(draft._id)}/sender-accounts`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emailAccountsByAie: sentEmailByAie, linkedinAccountIdsByAie: sentLinkedinByAie }),
        });
        const data = await resp.json();
        if (!data.ok) {
          toast.error(data.error ?? 'Failed to add the sender account');
          return;
        }
        appliedAssignments.current = assignments.map(a => ({ ...a }));
        appliedEmailAccountsByAie.current = sentEmailByAie;
        appliedLinkedinAccountIdsByAie.current = sentLinkedinByAie;
        // Adding a sender re-balances senders but regenerates nothing, so there
        // is no pending-regen to note — the "All changes saved" indicator is the
        // quiet confirmation. Warnings are real and always shown.
        for (const w of (data.warnings ?? []) as string[]) toast.warning(w);
      } catch {
        toast.error('Failed to add the sender account (network error)');
      } finally {
        setApplyingReps(false);
      }
      return;
    }

    setApplyingReps(true);
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(draft._id)}/sales-reps`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assignments,
          emailAccountsByAie: sentEmailByAie,
          linkedinAccountIdsByAie: sentLinkedinByAie,
        }),
      });
      const data = await resp.json();
      if (!data.ok) {
        // The endpoint refuses while an EARLIER edit's messages are still
        // regenerating (campaign back in 'generating'). That's a transient race,
        // not something the user did wrong.
        const stillGenerating = resp.status === 409 && /finished generating/.test(String(data.error ?? ''));
        if (stillGenerating) {
          // Retry the self-commit shortly, quietly — there is no button to press
          // again now that this applies itself.
          if (pendingRepsTimer.current) clearTimeout(pendingRepsTimer.current);
          pendingRepsTimer.current = setTimeout(() => void applySalesRepChanges({ auto: true }), 3000);
        } else {
          // A real failure must be visible even for the silent self-commit, or a
          // rep change the user believes is saved would be lost without a trace.
          toast.error(data.error ?? 'Failed to change Sales Reps');
        }
        return;
      }
      appliedAssignments.current = assignments.map(a => ({ ...a }));
      appliedEmailAccountsByAie.current = sentEmailByAie;
      appliedLinkedinAccountIdsByAie.current = sentLinkedinByAie;
      // One in-place indicator instead of a per-apply toast; the bundled toast
      // fires on exit.
      noteDeferredRegen(data.messagesQueued);
      // Leads whose outreach had already started keep sending from the LinkedIn
      // account that contacted them, because that is the only account connected
      // to them — so for these the split shown above is not the split that will
      // send. Said here rather than left in a server log: it is the one part of
      // a Sales Rep change that cannot be fixed by changing the numbers.
      const pinned = Number(data.leadsPinnedToPreviousAccount ?? 0);
      if (pinned > 0) {
        toast.warning(
          `${pinned} lead${pinned === 1 ? '' : 's'} had already been contacted and will keep sending from the ` +
            `previous Sales Rep's LinkedIn account — only that account is connected to them.`,
        );
      }
    } catch {
      toast.error('Failed to change Sales Reps (network error)');
    } finally {
      setApplyingReps(false);
    }
  };

  /**
   * Whether a valid, applicable Sales-Rep / sender-account change is sitting in
   * the draft waiting to be committed. Gathers the wizard's live state and
   * defers the decision to the pure `shouldAutoApplySalesReps` (unit-tested in
   * sender-account-plan). Drives both the debounced auto-commit below and the
   * commit-before-leave in attemptLeave.
   */
  const salesRepAutoApplyReady = (): boolean =>
    !!draft._id &&
    shouldAutoApplySalesReps({
      isEditingNonDraft,
      launched: !!draft.launchedAt,
      valid: salesRepApplyGate() === null,
      appliedAssignments: appliedAssignments.current,
      nextAssignments: liveDraft.current.aiEmployeeAssignments,
      appliedEmail: appliedEmailAccountsByAie.current,
      nextEmail: liveDraft.current.emailAccountsByAie,
      appliedLinkedin: appliedLinkedinAccountIdsByAie.current,
      nextLinkedin: liveDraft.current.linkedinAccountIdsByAie,
    });

  // Auto-apply Sales-Rep / sender-account changes in edit mode, so a rep change
  // commits itself like every other field instead of waiting on an "Apply"
  // button that could be forgotten (LinkedIn-UAT 4 Sep). Mirrors the 1.5s
  // autosave debounce and fires ONLY when salesRepAutoApplyReady() — a complete,
  // valid, applicable change. The dedicated /sales-reps endpoint stays the
  // single atomic writer; an incomplete picker keeps its quiet inline hint and
  // commits nothing. A change made in the last debounce window is still
  // committed on exit (see attemptLeave), so nothing is silently lost.
  useEffect(() => {
    if (campaignId && !loaded) return;
    if (applyingReps) return;
    if (!salesRepAutoApplyReady()) return;
    if (pendingRepsTimer.current) clearTimeout(pendingRepsTimer.current);
    pendingRepsTimer.current = setTimeout(() => void applySalesRepChanges({ auto: true }), 1500);
    return () => {
      if (pendingRepsTimer.current) {
        clearTimeout(pendingRepsTimer.current);
        pendingRepsTimer.current = null;
      }
    };
    // Deliberately NOT keyed on `applyingReps`: re-running when it flips false
    // would re-schedule a still-dirty change straight after a HARD failure —
    // an endless retry + toast loop. A change made mid-apply is instead caught
    // by the next edit or by the commit-before-leave in attemptLeave, so it is
    // delayed at worst, never lost. (The transient 409-while-generating case has
    // its own bounded retry inside applySalesRepChanges.)
  }, [draft.aiEmployeeAssignments, draft.emailAccountsByAie, draft.linkedinAccountIdsByAie, loaded]);

  // Every step writes through liveDraft.current, never this render's `draft`:
  // a step not shown is kept and not rendered again (keptSteps), so what it
  // finishes later — a search's interpretation, an import, accounts loading —
  // calls the callbacks it got when it was last shown. Spreading that render's
  // draft would undo whatever was changed on the step shown since (R17a).
  const stepContent = () => {
    const step = STEPS[currentStep]!;
    switch (step.key) {
      case 'type': {
        // Edit mode auto-applies rep / sender-account changes (the debounced
        // effect above) — no "Apply" button to forget. The ONE change that
        // cannot auto-apply is removing a sender account from a LAUNCHED
        // campaign: it would strand the leads already sending from it. Say so
        // inline, right where the pick was made, instead of silently dropping it.
        const launchedBlockedRemoval = launchedRemovalBlocked({
          editingNonDraft: isEditingNonDraft,
          launched: !!draft.launchedAt,
          applied: {
            assignments: appliedAssignments.current,
            emailAccountsByAie: appliedEmailAccountsByAie.current,
            linkedinAccountIdsByAie: appliedLinkedinAccountIdsByAie.current,
          },
          draft,
        });
        return html`
          ${
            launchedBlockedRemoval
              ? html`
                <div class="alert alert-warning mb-4 flex items-start gap-2">
                  <span class="icon-[material-symbols--info] mt-0.5 text-lg"></span>
                  <div class="text-sm">
                    A sender account can't be removed from a launched campaign — the leads already sending from it would
                    be stranded, so this change won't be saved. Re-tick the account; you can still ADD one.
                  </div>
                </div>
              `
              : nothing
          }
          ${WizCampaignType({
            emailAccountsByAie: draft.emailAccountsByAie,
            linkedinAccountIdsByAie: draft.linkedinAccountIdsByAie,
            aiEmployeeAssignments: draft.aiEmployeeAssignments,
            // Channels the sequence sends on — the step marks a rep whose
            // account picker for one of them is still empty, right where it
            // can be fixed, instead of failing on Apply.
            requiredChannels: requiredChannels(),
            // A rep covered by a Summary-step fallback borrows someone else's
            // account for that channel, so the hint has to stay quiet for them
            // — same rule the server validates by.
            channelFallbacks: draft.channelFallbacks ?? [],
            // Keep the per-rep "missing account" hints hidden until the user
            // tries to move on (set in goToStep). Reads as an error the instant
            // a rep is ticked otherwise — the exact thing UAT flagged 2026-09-19.
            revealAccountGaps: revealRepGaps,
            // See WizCampaignTypeProps.assignmentsReady: a brand-new draft has
            // nothing to wait for, but an existing campaign's real assignment
            // only becomes trustworthy once the load effect above has finished.
            assignmentsReady: !campaignId || loaded,
            crmAutoSync: draft.crmAutoSync ?? false,
            onCrmAutoSyncChange: enabled => setDraft({ ...liveDraft.current, crmAutoSync: enabled }),
            onAssignmentsChange: applyAssignmentsChange,
            // The flat lists follow as the union across reps (logic/draft.ts).
            onEmailAccountsByAieChange: emailAccountsByAie =>
              setDraft(withEmailAccountsByAie(liveDraft.current, emailAccountsByAie)),
            onLinkedinAccountIdsByAieChange: linkedinAccountIdsByAie =>
              setDraft(withLinkedinAccountIdsByAie(liveDraft.current, linkedinAccountIdsByAie)),
          })}
        `;
      }
      case 'offering':
        return WizOffering({
          offering: draft.offering,
          onOfferingChange: handleOfferingChange,
          onBusyChange: setOfferingBusy,
          uploadState: offeringUploadState,
          onUploadStateChange: setOfferingUploadState,
          parsedDocs: offeringParsedDocs,
          onParsedDocsChange: setOfferingParsedDocs,
        });
      case 'leads':
        // Leads already exist for this campaign (real documents with
        // generated messages) — the one-shot CSV/Apollo import tool below
        // (WizLeads) can't safely edit that list, so manage them through the
        // dedicated add/remove endpoints instead. Works during the launch
        // cool-down; every change restarts the hour.
        if (isEditingNonDraft) {
          return WizManageLeads({
            campaignId: draft._id!,
            hasApolloSearch: !!draft.apolloSearchInput,
            leads: draft.leads as any,
            onLeadsChange: leads => setDraft({ ...liveDraft.current, leads: leads as any }),
          });
        }
        return WizLeads({
          campaignName: draft.name,
          onCampaignNameChange: name => setDraft({ ...liveDraft.current, name }),
          leads: draft.leads,
          onLeadsChange: leads => setDraft({ ...liveDraft.current, leads }),
          // Derived from the current sequence — the user no longer picks a type.
          campaignType: deriveCampaignType(draft.sequence),
          apolloSearchInput: draft.apolloSearchInput,
          onApolloSearchInputChange: apolloSearchInput => setDraft({ ...liveDraft.current, apolloSearchInput }),
          projectId: draft.projectId,
          onProjectIdChange: projectId => setDraft({ ...liveDraft.current, projectId }),
        });
      case 'schedule':
        return WizSchedule({
          schedule: draft.schedule,
          sequence: draft.sequence,
          onScheduleChange: schedule => setDraft({ ...liveDraft.current, schedule }),
        });
      case 'sequence':
        // The same notice the connect step carries, once at the top of the
        // step as well — asked for after the eleven-day outage, because the
        // sequence editor is where the note is written and therefore the last
        // place the person can still act on what will happen to it. It renders
        // nothing at all unless there is something true to say, so a campaign
        // with no connect step and a fully paid set of seats sees no banner.
        return html`
          ${ConnectionNoteNotice({
            hasNoteOnConnectStep: draft.sequence.some(s => connectStepCarriesANote(s)),
            // Only the accounts this draft sends from. The flat list rather than
            // the per-rep map: the notice names accounts, and it does not care
            // which rep each one belongs to.
            sendingAccountIds: draft.linkedinAccountIds,
          })}
          ${WizSequence({
            sequence: draft.sequence,
            linkedinAccountIds: draft.linkedinAccountIds,
            previewAiEmployeeId: previewSenderId(draft),
            leads: draft.leads,
            offering: draft.offering,
            onSequenceChange: handleSequenceChange,
            autoReply: draft.autoReply ?? false,
            onAutoReplyChange: enabled => setDraft({ ...liveDraft.current, autoReply: enabled }),
            replyPrompt: draft.replyPrompt ?? '',
            onReplyPromptChange: prompt => setDraft({ ...liveDraft.current, replyPrompt: prompt }),
            // Not "is there any account in the draft" but "is every rep that sends
            // on this channel covered" — the flat lists miss a rep who borrows the
            // channel through a fallback, and the banner then contradicted the
            // navigation gate right next to it.
            hasEmailAccount: !salesRepAccountGaps().email,
            hasLinkedinAccount: !salesRepAccountGaps().linkedin,
            apolloSearchInput: draft.apolloSearchInput,
            language: draft.language ?? 'en',
            onLanguageChange: async language => {
              // ONE atomic update: set the language AND drop the stored AI step
              // previews (they are in the old language). AI cards regenerate
              // immediately — in the new language, honoring any refine prompt.
              setDraft(withLanguage(liveDraft.current, language));
              // MANUAL messages are sent verbatim (never AI-generated), so they are
              // NOT regenerated. Instead we translate the exact text into the new
              // language once per explicit language switch and store it back as the
              // manual body/subject. {{variables}} are preserved server-side. This
              // keeps both the collapsed and expanded views (which read manualBody)
              // consistently in the selected language.
              const manualSteps = manualStepsOf(liveDraft.current);
              // Tell the operator up front: translating a manual message is the one
              // AI touch on copy they wrote by hand, and it replaces that text.
              // Shown only when there is manual copy to translate.
              if (manualSteps.length > 0) {
                toast.info('Changing languages will translate your manually written messages');
              }
              let translationFailures = 0;
              for (const ms of manualSteps) {
                try {
                  const resp = await fetch('/api/prompt/translate', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ body: ms.manualBody, subject: ms.subject, channel: ms.channel, language }),
                  });
                  const data = (await resp.json().catch(() => null)) as {
                    ok: boolean;
                    body?: string;
                    subject?: string;
                  } | null;
                  if (data?.ok && data.body) {
                    setDraft(withTranslatedStep(liveDraft.current, ms.id, { body: data.body, subject: data.subject }));
                  } else {
                    translationFailures++;
                  }
                } catch {
                  translationFailures++;
                }
              }
              // A failed translation must NOT be silent — that is exactly the
              // "German campaign, English message" blocker. The manual copy stays
              // in the previous language; say so, so the operator fixes it before
              // launch rather than discovering it in a prospect's inbox.
              if (translationFailures > 0) {
                const one = translationFailures === 1;
                toast.error(
                  `Couldn't translate ${translationFailures} manual message${one ? '' : 's'} — ` +
                    `${one ? 'it stays' : 'they stay'} in the previous language. Edit ${one ? 'it' : 'them'} before launching.`,
                );
              }
            },
          })}
        `;
      case 'summary':
        // Editing an already-prepared campaign: there's nothing left to
        // "generate from scratch" — every change on the way here (offering,
        // schedule, step content, new steps, Sales Rep) was already saved
        // incrementally through its own safe endpoint. Calling
        // /api/campaign/prepare here would just 409 (it now refuses anything
        // past 'draft' — see fix/launch-cooldown-bugs) and would be
        // destructive if it somehow didn't. Just confirm and return.
        if (isEditingNonDraft) {
          return html`
            <div class="card bg-base-100">
              <div class="card-body items-center gap-3 py-10 text-center">
                <span class="icon-[material-symbols--check-circle] text-success text-4xl"></span>
                <h2 class="text-lg font-medium">Changes saved</h2>
                <p class="text-base-content/60 max-w-md text-sm">
                  Everything you changed (offering, schedule, sequence, Sales Rep) has already been saved
                  automatically. Return to the campaign to review the generated messages.
                </p>
                <button
                  class="btn btn-primary mt-2 gap-1.5"
                  @click=${() => {
                    setFinished(true);
                    wizardBeforeLeave = null;
                    sdrRouter.goTo('campaignDetail', { params: { campaignId: draft._id } });
                  }}
                >
                  <span class="icon-[material-symbols--arrow-back] text-base"></span>
                  Back to Campaign
                </button>
              </div>
            </div>
          `;
        }
        return WizSummary({
          draft,
          onPrepared: (campaignId: string) => {
            // Mark as finished so the wizard does not try to save the draft on leave
            // and skip the beforeleave guard. Then jump to the campaign detail page
            // where the user reviews/edits messages and finally launches the campaign.
            setFinished(true);
            wizardBeforeLeave = null;
            sdrRouter.goTo('campaignDetail', { params: { campaignId } });
          },
        });
    }
  };

  // The stepper and the Back / Next row (logic/steps.ts). The Sales Rep account
  // gate answers on click instead of greying "Next" out and restating the rep
  // picker's own hint one size louder underneath it (SALES_REP_ACCOUNT_BLOCK).
  const nav = stepNavigation(currentStep, offeringBusy, reasonAt, stepsHeld);
  const { canGoBack, canGoForward, blockReason, softBlocked } = nav;
  const stepper = stepperItems({
    draft,
    currentStep,
    maxStepReached,
    finished,
    offeringBusy,
    reasonAt,
    held: stepsHeld,
  });

  return html`
    <div class="mx-auto flex max-w-4xl flex-col gap-6 p-6">
      <div class="flex flex-col gap-4">
        <nav class="text-base-content/60 flex items-center gap-1 text-sm" aria-label="Breadcrumb">
          <a class="link link-hover" @click=${() => sdrRouter.goTo('dashboard')}>Overview</a>
          <span aria-hidden="true">›</span>
          <span class="text-base-content/80 font-medium" aria-current="page"
            >${campaignId ? 'Edit campaign' : 'New campaign'}</span
          >
        </nav>
        <div class="flex items-center gap-2">
          <button
            class="btn btn-ghost btn-sm btn-square"
            @click=${() => sdrRouter.goTo('dashboard')}
            aria-label="Back to dashboard"
            title="Back to dashboard"
          >
            <span class="icon-[material-symbols--arrow-back] text-lg" aria-hidden="true"></span>
          </button>
          <h1 class="text-[2rem] leading-10 font-medium tracking-tight">${campaignId ? 'Edit campaign' : 'New campaign'}</h1>
        </div>
        <div class="flex flex-wrap items-center gap-3">
          <p class="text-base-content/60">Follow the steps to configure and launch your outreach.</p>
          <div class="flex items-center gap-2">
            ${
              saving
                ? html`<span class="text-base-content/50 inline-flex items-center gap-1 text-xs italic">
                  <span class="loading loading-spinner loading-xs"></span> Saving…
                </span>`
                : saveError
                  ? html`<span class="text-error inline-flex items-center gap-1 text-xs" title=${saveError}>
                    <span class="icon-[material-symbols--cloud-off] text-sm"></span> Save failed — use Save to retry
                  </span>`
                  : lastSavedAt && !saveBlocked
                    ? html`<span class="text-base-content/50 inline-flex items-center gap-1 text-xs">
                      <span class="icon-[material-symbols--cloud-done] text-sm"></span> All changes saved
                    </span>`
                    : // Before an edited campaign has loaded there is nothing saved to speak of (R17a3).
                      html`<span class="text-base-content/40 text-xs"
                        >${
                          load.state === 'loading'
                            ? LOADING_NOTE
                            : load.state === 'failed'
                              ? NOT_LOADED_NOTE
                              : 'Not saved yet'
                        }</span
                      >`
            }
            ${
              // One quiet, in-place signal that edits will regenerate on exit —
              // replaces the per-change toasts that used to stack up. The single
              // bundled toast still fires on leave (flushPendingRegeneration).
              regenPending
                ? html`<span class="text-base-content/50 inline-flex items-center gap-1 text-xs">
                  <span class="icon-[material-symbols--autorenew] text-sm"></span> Messages will regenerate when you leave
                </span>`
                : nothing
            }
            <button
              class="btn btn-xs gap-1"
              ?disabled=${saving || !!saveBlocked}
              title=${saveBlocked ?? 'Save your progress now'}
              @click=${() => void persistDraft(draft)}
            >
              <span class="icon-[material-symbols--save] text-sm"></span> Save
            </button>
          </div>
        </div>
      </div>

      <!-- Stepper -->
      <div class="flex items-center gap-1">
        ${stepper.map((step, i) => {
          const { complete } = step;
          return html`
            ${
              i > 0
                ? html`<div
                  class="${step.lineDone ? 'bg-(--sdr-ink-muted)' : 'bg-base-300'} h-0.5 flex-1"
                ></div>`
                : ''
            }
            <button
              ?disabled=${step.disabled}
              aria-label=${step.label}
              title=${step.title}
              class="${
                // Read like the lead timeline (WP27e): the current step ringed in
                // navy, a finished one in ink, the rest grey.
                step.current
                  ? 'border-primary text-primary bg-base-100'
                  : complete
                    ? 'text-base-content border-transparent hover:bg-(--sdr-hover)'
                    : 'text-base-content/50 border-transparent hover:bg-(--sdr-hover)'
              } flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1.5 sm:px-3 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50"
              @click=${() => void goToStep(i)}
            >
              ${
                complete
                  ? html`<span class="icon-[material-symbols--check-circle] text-base"></span>`
                  : html`<span class="${STEP_ICONS[step.key]} text-base"></span>`
              }
              <!-- On a phone a step is its icon; the label is the button's name there. -->
              <span class="hidden sm:inline">${step.label}</span>
            </button>
          `;
        })}
      </div>

      <!-- Step content -->
      ${
        campaignId && load.state === 'loading'
          ? html`<div class="flex items-center justify-center py-20">
            <span class="loading loading-spinner loading-lg"></span>
          </div>`
          : load.state === 'failed'
            ? // Never the empty steps for a campaign that did not load (R17a3): the campaign page's card
              // for the same failure (R16a), the reason, and Try again.
              loadFailure(LOAD_FAILED_TITLE, load.reason, () => setLoad({ state: 'loading' }))
            : // A step not shown keeps its elements and its state, hidden, and is
              // not rendered again (noChange) until it is shown again.
              repeat(
                keptSteps(visitedSteps, currentStep),
                step => step,
                step =>
                  html`<div class="ndi-step-enter" ?hidden=${step !== currentStep}>
                  ${step === currentStep ? stepContent() : noChange}
                </div>`,
              )
      }
      ${
        nav.banner?.kind === 'busy'
          ? html`<div class="alert alert-info py-2 text-sm">
            <span class="loading loading-spinner loading-xs"></span>
            <span
              >Processing your document/website — you can continue once it's finished so the result isn't lost.</span
            >
          </div>`
          : nav.banner?.kind === 'blocked'
            ? html`<div class="alert alert-warning py-2 text-sm">
              <span class="icon-[material-symbols--info] text-lg"></span>
              <span>${nav.banner.reason}</span>
            </div>`
            : nothing
      }

      <!-- Navigation -->
      <div class="flex items-center justify-between">
        <button
          class="btn btn-ghost ${canGoBack ? '' : 'invisible'} gap-1"
          ?disabled=${offeringBusy}
          title=${offeringBusy ? 'Please wait until the document/website finishes processing.' : ''}
          @click=${() => void goToStep(currentStep - 1)}
        >
          <span class="icon-[material-symbols--arrow-back]"></span>
          ${nav.backLabel}
        </button>
        ${
          canGoForward
            ? html`
              <button
                class="btn btn-primary gap-1"
                ?disabled=${!!blockReason && !softBlocked}
                title=${blockReason ?? ''}
                @click=${() => void goToStep(currentStep + 1)}
              >
                ${nav.nextLabel}
                <span class="icon-[material-symbols--arrow-forward]"></span>
              </button>
            `
            : nothing
        }
      </div>
    </div>
  `;
});
